import type { ReactNode } from "react";

import { GlowCard, type GlowCardProps } from "./ui/glow-card.js";
import { Badge, type BadgeProps } from "./ui/badge.js";
import { Skeleton } from "./Skeleton.js";
import { cn } from "../lib/utils.js";
import { avatarBgClass, fmt, pendingRequestTotal, type TeamOverviewRow } from "../pages/AttendancePage.js";

const WEEK_STATUS: Record<string, { label: string; tone: NonNullable<BadgeProps["tone"]> }> = {
  APPROVED: { label: "Approved", tone: "success" },
  REJECTED: { label: "Declined", tone: "error" },
  PENDING: { label: "Pending Review", tone: "warning" },
};
const NOT_SUBMITTED = { label: "Not Submitted", tone: "warning" } as const;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

export interface TeamMemberCardProps {
  person: TeamOverviewRow;
  /**
   * "review": a manager's own Team tab -- counts read as requests waiting on
   * the viewer. "directory": the department browser -- neutral counts, plus
   * the person's own hours.
   */
  context?: "review" | "directory";
  /** Makes the whole card the click target. Ignored when `actions` is set. */
  onSelect?: () => void;
  selected?: boolean;
  /** Footer buttons. The card then becomes a plain container (buttons can't nest). */
  actions?: ReactNode;
  /**
   * Card look, adjustable from the call site -- any of these can also be computed
   * at runtime (e.g. glow: pending ? "high" : "low"). Omitted values fall back to
   * glowCardDefaults; size defaults to "sm" for review and "md" for directory.
   */
  card?: Pick<
    GlowCardProps,
    | "size" | "glow" | "spotlight" | "spotlightSize" | "tone" | "reach" | "spread" | "radius" | "interaction"
    | "width" | "height" | "minWidth" | "minHeight" | "maxWidth" | "maxHeight"
  >;
  className?: string;
}

export function TeamMemberCard({ person, context = "review", onSelect, selected, actions, card, className }: TeamMemberCardProps) {
  const pendingTotal = pendingRequestTotal(person);
  const status = WEEK_STATUS[person.thisWeek.status ?? ""] ?? NOT_SUBMITTED;
  const checkedIn = !!person.today?.checkIn && !person.today?.checkOut;
  const checkedOut = !!person.today?.checkOut;
  const clickable = !actions && !!onSelect;

  return (
    <GlowCard
      size={context === "review" ? "sm" : "md"}
      {...card}
      as={clickable ? "button" : "div"}
      onClick={clickable ? onSelect : undefined}
      selected={selected}
      className={cn("flex flex-col items-start", className)}
    >
      <div className="mb-2 flex items-center gap-2.5">
        <div className={cn("flex size-9 shrink-0 items-center justify-center rounded-full text-[13px] font-bold text-white", avatarBgClass(person.id))}>
          {person.firstName[0]}{person.lastName[0]}
        </div>
        <div>
          <div className="text-sm font-semibold">{person.firstName} {person.lastName}</div>
          <div className="text-xs text-surface-700">{person.employeeCode} &middot; {person.designation}</div>
          {/*
            Only the department endpoints send `office`, and there it is the point: a
            department's people are spread across branches, so each card has to name its
            own. `null` is a real answer (nobody set one) and says so; `undefined` means
            this caller doesn't deal in offices, so the line is left off entirely.
          */}
          {person.office !== undefined && (
            <div className="text-xs text-surface-500">{person.office?.name ?? "No office set"}</div>
          )}
        </div>
      </div>

      {/*
        "review" is a manager's own queue, so it carries the full attendance read-out.
        "directory" is the department browser, where the card only has to identify someone
        and say how big their team is — hours, check-in state and the week's sign-off are
        all one click away on their record, and repeated across a grid they were noise.
      */}
      {context === "review" && (
        <>
          <div className="mb-1 flex items-center gap-1.5 text-xs text-surface-700">
            <span className={cn("size-1.5 rounded-full", checkedIn ? "bg-success" : checkedOut ? "bg-surface-400" : "bg-surface-300")} />
            {checkedIn ? `Checked In · ${fmt(person.today!.checkIn)}` : checkedOut ? `Checked Out · ${fmt(person.today!.checkOut)}` : "Not Checked In"}
          </div>
          <div className="mb-2 text-xs text-surface-700">
            {plural(person.directReportCount, "direct report")}
            {" "}&middot; Team hours: <strong>{person.teamHours.totalHours.toFixed(1)}</strong> / {person.teamHours.expectedHours.toFixed(1)} hrs
          </div>
          <Badge tone={status.tone}>{status.label}</Badge>
        </>
      )}

      {context === "directory" && person.directReportCount > 0 && (
        <div className="text-xs text-surface-700">{person.directReportCount} in team</div>
      )}

      {/*
        Only the roster's "Other Members" group sends this. Those people sit outside every
        manager in the department, which is not the same as having no manager — so the card
        names the one they do have, with their department when it is a different one.
      */}
      {person.reportsTo !== undefined && (
        <div className="text-xs text-surface-700">
          {person.reportsTo
            ? <>
                {/* An HR manager is not a reporting line, so it never borrows that wording. */}
                {person.reportsTo.kind === "reporting" ? "Reports to " : "HR manager: "}
                <span className="font-medium">{person.reportsTo.name}</span>
                {person.reportsTo.departmentName && <span className="text-surface-500"> &middot; {person.reportsTo.departmentName}</span>}
              </>
            : <span className="text-warning">No manager assigned</span>}
        </div>
      )}

      {pendingTotal > 0 && (
        <div className="mt-2 text-xs text-warning">
          &bull; {context === "review" ? `${plural(pendingTotal, "request")} need your review` : `${plural(pendingTotal, "pending request")}`}
        </div>
      )}

      {actions && <div className="mt-3 flex flex-wrap gap-2">{actions}</div>}
    </GlowCard>
  );
}

/** Loading placeholder with the same footprint as a TeamMemberCard. */
export function TeamMemberCardSkeleton() {
  return (
    <GlowCard interaction="none" size="md">
      <div className="mb-2 flex items-center gap-2.5">
        <Skeleton width={36} height={36} borderRadius="50%" />
        <div className="flex flex-1 flex-col gap-1.5">
          <Skeleton width="70%" height={14} />
          <Skeleton width="50%" height={11} />
        </div>
      </div>
      <div className="mb-2"><Skeleton width="60%" height={12} /></div>
      <Skeleton width={90} height={20} borderRadius="var(--radius-full)" />
    </GlowCard>
  );
}
